// Outlet context for AdminShell. Designed as a discriminated union so the
// future aggregated view (/admin/all/*) can carry a different payload
// without rewriting page-level call sites.
//
// Today every page only ever runs in mode='single'. Pages that don't make
// sense in aggregated mode (channel-settings, station-specific detail
// pages) read via useSingleStationContext() which throws on aggregated.
// Pages that *will* support aggregated views read via useAdminContext()
// and switch on `ctx.mode`.

import { useOutletContext } from 'react-router-dom';
import type { PublisherConfig } from '../../../types/selfService';

// Loose mirror of useAuth's SelfServiceUser. Kept structural so the shell
// doesn't need to import the full type — pages that already use useAuth
// for additional fields can keep doing so.
export interface AdminUser {
  id: string;
  email: string;
  rawRole?: string;
}

export type AdminContext =
  | { mode: 'single'; config: PublisherConfig; slug: string; user: AdminUser }
  | { mode: 'aggregated'; stations: PublisherConfig[]; user: AdminUser }; // FUTURE: /admin/all/*

export function useAdminContext(): AdminContext {
  return useOutletContext<AdminContext>();
}

// Convenience for the >90% case of "I only handle single-station mode."
// Throws (instead of silently returning a stale value) when accessed from
// an aggregated route, which makes the missing-implementation obvious as
// the aggregated view rolls out.
export function useSingleStationContext() {
  const ctx = useOutletContext<AdminContext>();
  if (ctx.mode !== 'single') {
    throw new Error(
      'This page expects single-station context. ' +
        'Add an aggregated-mode branch in the page, or update the route to only match /admin/:slug.',
    );
  }
  return ctx;
}
